import { useCallback, useMemo, useState } from 'react';

import { useProposalApi } from 'api/projectVersioning/proposal-api';
import { resolve } from 'api/resolve';
import { useFetchCurrentSubscription } from 'api/subscriptions/hooks/useFetchCurrentSubscription';
import { useUserEmail } from 'state/appState/appStateHooks';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

import type { EligibleUser } from '../components/dialogs/ActionBlockedDialog';

type BlockedState = {
  action: 'merge' | 'discard';
  users: EligibleUser[];
};

export function useEligibilityGate(projectId: string, proposalId: string) {
  const [blockedState, setBlockedState] = useState<BlockedState | null>(null);
  const userEmail = useUserEmail();
  const { getEligibleMergers } = useProposalApi();
  const { data: currentSubscription } = useFetchCurrentSubscription();

  const subscriptionUserNameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    currentSubscription?.users
      .filter((user) => user.identityUser !== null)
      .forEach(({ userEmail: email, identityUser }) => {
        map.set(email.toLowerCase(), `${identityUser.firstName} ${identityUser.lastName}`.trim());
      });
    return map;
  }, [currentSubscription]);

  const checkAndOpen = useCallback(
    async (action: 'merge' | 'discard', onEligible: () => void) => {
      if (!projectId || !proposalId) return;

      try {
        const result = await resolve(getEligibleMergers(projectId, proposalId));
        const users = result?.users ?? [];
        const normalizedUserEmail = userEmail?.toLowerCase();
        const isEligible = users.some((email) => email.toLowerCase() === normalizedUserEmail);

        if (isEligible) {
          onEligible();
        } else {
          setBlockedState({
            action,
            users: users.map((email) => ({
              email,
              name: subscriptionUserNameByEmail.get(email.toLowerCase()) ?? email
            }))
          });
        }
      } catch {
        SNACKBAR_STORE.getState().showSnackbar(`Failed to check ${action} permissions`, 'error', true);
      }
    },
    [projectId, proposalId, getEligibleMergers, userEmail, subscriptionUserNameByEmail]
  );

  const clearBlocked = useCallback(() => setBlockedState(null), []);

  return { blockedState, checkAndOpen, clearBlocked };
}
